import { useState } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts';

interface ResidualPlotProps {
  residuals: number[];
  fittedValues: number[];
}

export function ResidualPlot({ residuals, fittedValues }: ResidualPlotProps) {
  const [plotTab, setPlotTab] = useState<'chart' | 'explanation'>('chart');

  // Prepare data for scatter plot (residuals vs fitted values)
  const scatterData = residuals.map((residual, index) => ({
    fitted: fittedValues[index],
    residual,
  }));

  // Calculate residual statistics
  const mean = residuals.reduce((sum, r) => sum + r, 0) / residuals.length;
  const variance = residuals.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / residuals.length;
  const stdDev = Math.sqrt(variance);

  // Check for patterns (simple autocorrelation test)
  let positiveCount = 0;
  for (let i = 1; i < residuals.length; i++) {
    if ((residuals[i] > 0 && residuals[i - 1] > 0) || (residuals[i] < 0 && residuals[i - 1] < 0)) {
      positiveCount++;
    }
  }
  const autocorrelationRatio = positiveCount / (residuals.length - 1);

  const hasPattern = autocorrelationRatio > 0.7; // High correlation suggests pattern

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="text-sm">
            <strong>Fitted:</strong> {payload[0].value.toFixed(3)}
          </p>
          <p className="text-sm">
            <strong>Residual:</strong> {payload[1].value.toFixed(3)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Residual Analysis</h2>
        <div className="flex items-center space-x-3">
          <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200">
            <button
              type="button"
              onClick={() => setPlotTab('chart')}
              className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer ${
                plotTab === 'chart'
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              Plot & Analysis
            </button>
            <button
              type="button"
              onClick={() => setPlotTab('explanation')}
              className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer ${
                plotTab === 'explanation'
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              What is a Residual?
            </button>
          </div>
          <button
            onClick={() => {
              alert("Want automated AI explanations for Residuals? Request this feature in the feedback form at the bottom of the page!");
              document.getElementById('feedback-form')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center space-x-1 cursor-pointer"
          >
            <span>💡</span>
            <span>Request AI features</span>
          </button>
        </div>
      </div>

      {plotTab === 'chart' ? (
        <>
          {/* Residual Plot */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">
          Residuals vs Fitted Values
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="fitted"
              name="Fitted"
              tick={{ fontSize: 12 }}
              label={{ value: 'Fitted Values', position: 'bottom', offset: 0 }}
            />
            <YAxis
              type="number"
              dataKey="residual"
              name="Residual"
              tick={{ fontSize: 12 }}
              label={{ value: 'Residuals', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="#ef4444" strokeWidth={2} strokeDasharray="3 3" />
            <Scatter
              data={scatterData}
              fill="#3b82f6"
              fillOpacity={0.6}
              name="Residuals"
            />
          </ScatterChart>
        </ResponsiveContainer>
        <p className="text-xs text-gray-600 mt-2 text-center">
          Residuals should be randomly scattered around zero with no clear pattern
        </p>
      </div>

      {/* Residual Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-xs text-gray-600 uppercase tracking-wide">Mean</div>
          <div className="text-xl font-bold text-gray-900">{mean.toFixed(4)}</div>
          <div className="text-xs text-gray-500">Should be ≈ 0</div>
        </div>

        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-xs text-gray-600 uppercase tracking-wide">Std Dev</div>
          <div className="text-xl font-bold text-gray-900">{stdDev.toFixed(4)}</div>
          <div className="text-xs text-gray-500">Spread of errors</div>
        </div>

        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-xs text-gray-600 uppercase tracking-wide">Min</div>
          <div className="text-xl font-bold text-gray-900">
            {Math.min(...residuals).toFixed(4)}
          </div>
          <div className="text-xs text-gray-500">Largest negative</div>
        </div>

        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-xs text-gray-600 uppercase tracking-wide">Max</div>
          <div className="text-xl font-bold text-gray-900">
            {Math.max(...residuals).toFixed(4)}
          </div>
          <div className="text-xs text-gray-500">Largest positive</div>
        </div>
      </div>

      {/* Diagnostic Messages */}
      <div className="space-y-3">
        {/* Mean check */}
        <div className={`p-3 rounded-lg border ${Math.abs(mean) < 0.01 ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
          <div className="flex items-start">
            <span className="text-lg mr-2">{Math.abs(mean) < 0.01 ? '✓' : '⚠️'}</span>
            <div className="flex-1">
              <div className="font-semibold text-sm">Residual Mean</div>
              <div className="text-xs text-gray-700">
                {Math.abs(mean) < 0.01
                  ? 'Good: Residuals are centered around zero, indicating unbiased predictions.'
                  : 'Caution: Residuals mean is not close to zero, suggesting potential bias in the model.'}
              </div>
            </div>
          </div>
        </div>

        {/* Pattern check */}
        <div className={`p-3 rounded-lg border ${!hasPattern ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
          <div className="flex items-start">
            <span className="text-lg mr-2">{!hasPattern ? '✓' : '⚠️'}</span>
            <div className="flex-1">
              <div className="font-semibold text-sm">Residual Pattern</div>
              <div className="text-xs text-gray-700">
                {!hasPattern
                  ? 'Good: Residuals appear random with no clear autocorrelation pattern.'
                  : `Caution: Detected potential autocorrelation (${(autocorrelationRatio * 100).toFixed(0)}% sequential similarity). Consider adding more model complexity.`}
              </div>
            </div>
          </div>
        </div>

        {/* Distribution check */}
        <div className="p-3 rounded-lg border bg-blue-50 border-blue-200">
          <div className="flex items-start">
            <span className="text-lg mr-2">ℹ️</span>
            <div className="flex-1">
              <div className="font-semibold text-sm">Interpretation</div>
              <div className="text-xs text-gray-700">
                For a good model, residuals should: (1) have mean ≈ 0, (2) be randomly scattered with constant variance, and (3) show no patterns or trends over time.
              </div>
            </div>
          </div>
        </div>
      </div>
        </>
      ) : (
        <div className="bg-slate-50 border border-gray-200/60 rounded-xl p-5 text-xs text-gray-600 leading-relaxed space-y-3">
          <p className="font-bold text-sm text-gray-800">Understanding Residuals in Forecasting</p>
          <p>
            A <strong>Residual</strong> (or error) is the difference between an actual observed value and the value predicted by the forecasting model:
            <br />
            <code className="bg-gray-250/60 px-1.5 py-0.5 rounded text-[11px] font-mono border border-gray-300">Residual = Actual Value - Fitted Value</code>
          </p>
          <p>
            Analyzing residuals is a critical diagnostic step to check if your model has successfully captured all systematic patterns in the data:
          </p>
          <ul className="list-disc list-inside space-y-1.5 pl-1 text-[11px] text-gray-500">
            <li><strong>Random Scattering:</strong> The points should form a horizontal band of constant width around the zero line. If you see patterns (e.g. a curve or funnel shape), the model is missing some trend, seasonality, or variance structure.</li>
            <li><strong>Zero Mean:</strong> The mean of residuals should be very close to zero. A non-zero mean indicates that the model is consistently over-predicting or under-predicting.</li>
            <li><strong>Autocorrelation:</strong> Sequential errors should not be correlated. If a positive error is consistently followed by another positive error, it indicates residual autocorrelation (pattern) that could be resolved with extra autoregressive parameters.</li>
          </ul>
        </div>
      )}
    </div>
  );
}
